import { selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, Form, Input, Modal, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateComplicity, useUpdateComplicity } from "../../../../common/api/queries/complicity";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import InstitutionSelect from "../../../enumerations/components/form/InstitutionSelect";
import { InstitutionType } from "../../../institution/enums";
import { ComplicityEnum } from "../../enums";
import { Complicity, CreateUpdateComplicity } from "../../types";

interface Props {
  complicity?: Complicity;
  open: boolean;
  onFormCancel: () => void;
}

const ComplicityForm = ({ complicity, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateComplicity>();

  const createComplicity = useCreateComplicity();
  const updateComplicity = useUpdateComplicity();

  useEffect(() => {
    if (open && complicity) {
      form.setFieldsValue({ ...complicity, institutionId: complicity.institution.id });
    }
  }, [complicity, open, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (complicity) {
          updateComplicity.mutate(
            { id: complicity.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "complicity", error.violations),
            },
          );
        } else {
          createComplicity.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "complicity", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={complicity ? t("complicity.titles.updateComplicity") : t("complicity.titles.createComplicity")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createComplicity.isPending || updateComplicity.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="complicityForm">
        <HiddenInput name="optimisticLockVersion" />
        <Row gutter={rowGutter}>
          <Col span={12}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionId",
                label: t("complicity.attrs.institutionId"),
                rules: [validations.notNull],
              }}
              optionsProps={{
                includeDeactivated: true,
                filterType: InstitutionType.INSURANCE_COMPANY,
              }}
            />
          </Col>

          <Col span={12}>
            <Form.Item
              name="complicityEnum"
              label={t("complicity.enums.complicityEnum._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.keys(ComplicityEnum).map((complicity) => ({
                  value: complicity,
                  label: t("complicity.enums.complicityEnum." + complicity),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item
              name="value"
              label={t("complicity.attrs.value")}
              rules={[validations.notBlank, validations.size(1, 64)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="deactivated"
              valuePropName="checked"
              rules={[validations.none]}
              className="form-item-without-label"
              initialValue={false}
            >
              <Checkbox>{t("complicity.attrs.deactivated")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ComplicityForm;
